// istanbul ignore file — composition shell; logic lives in hooks, slices, and child modules.

import { createElement } from 'react'
import { MemoryRouter, Route, useLocation } from 'react-router-dom'

import {
  Branding,
  Dashboard,
  NotetakerSettingsPage,
  SecurityPage,
} from 'features/admin/pages'
import { Billing } from 'features/billing'
import { ContactsSettingsPage } from 'features/crm'
import { ManagedEventTypesRoutes as ManagedEventTypes } from 'features/managed_event_types/routes'
import {
  BrandingPage,
  LoginPreferencesPage,
  MeetingIntelligencePage,
  MyLinkPage,
  ProfileSettingsPage,
  SchedulingAssistantPage,
  SecurityPage as PersonalSecurityPage,
  WorkflowsPage,
} from 'features/personal/pages'

import {
  SETTINGS_MODAL_PANE_KIND,
  SETTINGS_SECTION,
  parseSettingsTabFromSearch,
} from './constants'
import { SettingsModalAccessPage } from './settings_modal_access_page'
import {
  SETTINGS_MODAL_AI_TAB,
  normalizeSettingsModalAiTab,
  useShowSettingsModalAdminNotetakerAccess,
} from './settings_modal_ai_page'
import { SettingsModalCalendarMemoryPage } from './settings_modal_calendar_memory_page'
import { SettingsModalMemoryRouteShell } from './settings_modal_memory_route_shell'
import { resolveMemoryRouteInitialPath } from './settings_modal_memory_tabs'
import { SettingsModalOrgDirectoryPage } from './settings_modal_org_directory_page'
import { SettingsModalPeoplePage } from './settings_modal_people_page'
import { getSettingsSectionRenderContract } from './settingsConfig'

const SettingsModalAiPage = ({ settingsTab }) => {
  const location = useLocation()
  const showAdminNotetakerAccessTab = useShowSettingsModalAdminNotetakerAccess()
  const activeTab = normalizeSettingsModalAiTab(
    settingsTab ?? parseSettingsTabFromSearch(location.search),
    { showAdminNotetakerAccessTab },
  )

  if (activeTab === SETTINGS_MODAL_AI_TAB.NOTETAKER_ACCESS) {
    return (
      <div data-testid="settings-modal-ai-page">
        <MemoryRouter initialEntries={['/app/admin/notetaker/access']} initialIndex={0}>
          <Route path="/app/admin/notetaker" component={NotetakerSettingsPage} />
        </MemoryRouter>
      </div>
    )
  }

  return (
    <div data-testid="settings-modal-ai-page">
      {activeTab === SETTINGS_MODAL_AI_TAB.NOTETAKER
        ? <MeetingIntelligencePage />
        : <SchedulingAssistantPage />}
    </div>
  )
}

/**
 * @typedef {'column' | 'memory_route'} SettingsModalPaneKind
 * @see {@link ./constants.SETTINGS_MODAL_PANE_KIND} — canonical string values
 */

/**
 * What to render in the settings modal main column for a given section.
 * @typedef {object} SettingsModalPaneDescriptor
 * @property {SettingsModalPaneKind} kind
 * @property {boolean} [showFooterHost]
 * @property {import('react').ComponentType<Record<string, unknown>>} [component] — direct column child (kind column)
 * @property {Record<string, unknown>} [componentProps] — props passed to direct column child (kind column)
 * @property {string} [basePath] — {@link Route} path prefix (kind memory_route)
 * @property {string} [initialPath] — MemoryRouter first entry when not `basePath` (e.g. permissions default tab)
 * @property {string} [remountKey] — React `key` for `<MemoryRouter>` (kind memory_route); when
 *   omitted defaults to `basePath`. Calendar overrides this to remount on `initialPath` change.
 * @property {string} [dataTestIdOverride] — force main column test id (e.g. unknown section → Profile)
 */

/**
 * @typedef {object} SettingsModalResolverCtx
 * @property {Set<string>} adminEnabledSections — sections present in the admin nav
 * @property {string | null} [settingsTab] — `settings_tab=` slug for memory-route initial path
 * @property {string | undefined} [personalSecurityMemoryInitialPath] — first allowed Personal Security tab path
 * @property {string | undefined} [accessRegistryInitialPath] — flag-dependent default memory path for {@link SETTINGS_SECTION.adminAccess}
 */

/**
 * @typedef {object} SettingsModalColumnPaneRow
 * @property {import('react').ComponentType<Record<string, unknown>>} component
 * @property {boolean} showFooterHost — whether the modal renders the portal-able save/cancel footer host
 */

/**
 * @typedef {object} SettingsModalMemoryRouteRow
 * @property {import('react').ComponentType<Record<string, unknown>>} component
 * @property {string} basePath — `Route` path prefix (must match the page's nested admin/personal routes)
 * @property {string | null} adminGate — when non-null, the row is only used if `adminEnabledSections.has(adminGate)`
 * @property {boolean} showFooterHost
 * @property {(ctx: SettingsModalResolverCtx) => string | undefined} [getRegistryInitialPath]
 *   Per-render default memory path (e.g. flag-driven Personal Security tab, Admin Access landing).
 * @property {boolean} [remountOnInitialPathChange] — when true, `MemoryRouter` remounts whenever
 *   `initialPath` changes (instead of navigating in-memory). Needed for sections whose external
 *   tab anchors update the browser URL _first_ (today: personal Calendar — Calendar / Availability
 *   / Advanced tabs are CUI {@link Tab} `RouterLink`s wired to the browser history).
 */

/**
 * Hydrates {@link import('./settingsConfig').SettingsColumnPaneId} → page component + footer-host policy.
 *
 * Footer host: only the personal Branding page renders a portaled Save/Cancel; everything else
 * either has no save flow or uses the page's own footer.
 *
 * @type {Readonly<Record<string, SettingsModalColumnPaneRow>>}
 */
const COLUMN_PANE_BY_ID = {
  admin_dashboard: { component: Dashboard, showFooterHost: false },
  admin_org_branding: { component: Branding, showFooterHost: false },
  admin_people: { component: SettingsModalPeoplePage, showFooterHost: false },
  org_directory: { component: SettingsModalOrgDirectoryPage, showFooterHost: false },
  ai_settings: { component: SettingsModalAiPage, showFooterHost: true },
  branding: { component: BrandingPage, showFooterHost: true },
  link: { component: MyLinkPage, showFooterHost: false },
  login_preferences: { component: LoginPreferencesPage, showFooterHost: false },
  workflows: { component: WorkflowsPage, showFooterHost: false },
  // Legacy alias defense-in-depth; canonical Callie path is `ai_settings` → SettingsModalAiPage.
  assistant: { component: SchedulingAssistantPage, showFooterHost: false },
  contacts_settings: { component: ContactsSettingsPage, showFooterHost: false },
}

/**
 * Hydrates {@link import('./settingsConfig').SettingsMemoryRouteKey} → memory shell descriptor.
 *
 * `adminGate` null means no admin-nav check (personal pages). Memory-route admin pages fall back to
 * the Profile pane when the gate fails.
 *
 * `getRegistryInitialPath(ctx)` is consulted when the section's `settings_tab=` slug is absent or
 * unknown — it lets a row inject a per-render default path (e.g. flag-driven). Personal Security
 * uses this to defer to the first flag-allowed tab; Admin Access uses it to flip between
 * `domain_control` and `user_access` defaults.
 *
 * @type {Readonly<Record<string, SettingsModalMemoryRouteRow>>}
 */
const MEMORY_ROUTE_BY_KEY = {
  admin_access: {
    adminGate: SETTINGS_SECTION.adminAccess,
    basePath: '/app/admin/access',
    component: SettingsModalAccessPage,
    showFooterHost: false,
    getRegistryInitialPath: (ctx) => ctx.accessRegistryInitialPath,
  },
  admin_billing: {
    adminGate: SETTINGS_SECTION.adminBilling,
    basePath: '/app/admin/billing',
    component: Billing,
    showFooterHost: false,
  },
  admin_security: {
    adminGate: SETTINGS_SECTION.adminSecurity,
    basePath: '/app/admin/security',
    component: SecurityPage,
    showFooterHost: false,
  },
  admin_notetaker: {
    adminGate: SETTINGS_SECTION.adminNotetaker,
    basePath: '/app/admin/notetaker',
    component: NotetakerSettingsPage,
    showFooterHost: false,
  },
  admin_managed_event_types: {
    adminGate: SETTINGS_SECTION.adminManagedEventTypes,
    basePath: '/app/admin/managed_event_types',
    component: ManagedEventTypes,
    showFooterHost: false,
  },
  personal_security: {
    adminGate: null,
    basePath: '/app/personal/security',
    component: PersonalSecurityPage,
    showFooterHost: false,
    getRegistryInitialPath: (ctx) => ctx.personalSecurityMemoryInitialPath,
  },
  personal_profile: {
    adminGate: null,
    basePath: '/app/personal/profile',
    component: ProfileSettingsPage,
    showFooterHost: true,
  },
  personal_calendar: {
    adminGate: null,
    basePath: '/app/availability',
    component: SettingsModalCalendarMemoryPage,
    showFooterHost: false,
    remountOnInitialPathChange: true,
  },
}

/**
 * Build the memory-route descriptor for `row` given the resolver context. Hoisted so the resolver
 * and the profile fallback share one code path.
 *
 * @param {SettingsModalMemoryRouteRow} row
 * @param {string} effectiveSec
 * @param {SettingsModalResolverCtx} ctx
 * @returns {SettingsModalPaneDescriptor}
 */
function buildMemoryRouteDescriptor(row, effectiveSec, ctx) {
  const initialPath = resolveMemoryRouteInitialPath({
    section: effectiveSec,
    settingsTab: ctx.settingsTab ?? null,
    basePath: row.basePath,
    registryInitialPath: row.getRegistryInitialPath?.(ctx),
  })
  return {
    kind: SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE,
    basePath: row.basePath,
    initialPath,
    remountKey: row.remountOnInitialPathChange
      ? `${row.basePath}:${initialPath}`
      : row.basePath,
    component: row.component,
    showFooterHost: row.showFooterHost,
  }
}

/**
 * Pure resolver: which pane descriptor applies for this effective section and admin nav
 * availability. Intrinsic render strategies come from {@link ./settingsConfig}; this module
 * supplies components, paths, and footer-host policy.
 *
 * Resolution order:
 *
 * 1. Column pane → look up component in {@link COLUMN_PANE_BY_ID}.
 * 2. Memory route → look up row in {@link MEMORY_ROUTE_BY_KEY}; admin gate must pass.
 * 3. Otherwise (no contract, missing row, or gated admin route the user can't access) → fall back
 *    to the Personal Profile memory route with `dataTestIdOverride: 'settings-modal-body-profile'`.
 *
 * @param {string} effectiveSec — `SETTINGS_SECTION` value
 * @param {SettingsModalResolverCtx} ctx
 * @returns {SettingsModalPaneDescriptor}
 */
export function resolveSettingsModalPaneDescriptor(effectiveSec, ctx) {
  const contract = getSettingsSectionRenderContract(effectiveSec)

  if (contract?.kind === SETTINGS_MODAL_PANE_KIND.COLUMN) {
    const row = COLUMN_PANE_BY_ID[contract.pane]
    if (row != null) {
      const showFooterHost = row.showFooterHost
        && (contract.pane !== 'ai_settings' || ctx.settingsTab === SETTINGS_MODAL_AI_TAB.NOTETAKER)
      return {
        kind: SETTINGS_MODAL_PANE_KIND.COLUMN,
        component: row.component,
        componentProps: contract.pane === 'ai_settings'
          ? { settingsTab: ctx.settingsTab ?? null }
          : undefined,
        showFooterHost,
      }
    }
  }

  if (contract?.kind === SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE) {
    const row = MEMORY_ROUTE_BY_KEY[contract.routeKey]
    const gateOk = row != null && (row.adminGate == null || ctx.adminEnabledSections.has(row.adminGate))
    if (gateOk) {
      return buildMemoryRouteDescriptor(row, effectiveSec, ctx)
    }
  }

  return {
    ...buildMemoryRouteDescriptor(MEMORY_ROUTE_BY_KEY.personal_profile, effectiveSec, ctx),
    dataTestIdOverride: 'settings-modal-body-profile',
  }
}

/**
 * Renders the main-pane body for `resolveSettingsModalPaneDescriptor` (column / memory_route).
 * @param {SettingsModalPaneDescriptor} descriptor
 * @param {object} [options]
 * @param {import('react-router').History} [options.browserHistory]
 * @param {import('react-router').Location} [options.browserLocation]
 * @param {Record<string, unknown>} [options.componentProps]
 * @param {string | null} [options.settingsSection] — effective settings section (for tab URL sync)
 * @returns {import('react').ReactNode}
 */
export function renderSettingsModalPaneBody(descriptor, options = {}) {
  const {
    browserHistory,
    browserLocation,
    componentProps,
    settingsSection,
  } = options
  switch (descriptor.kind) {
    case SETTINGS_MODAL_PANE_KIND.MEMORY_ROUTE:
      return createElement(SettingsModalMemoryRouteShell, {
        basePath: descriptor.basePath,
        initialPath: descriptor.initialPath,
        remountKey: descriptor.remountKey,
        component: descriptor.component,
        browserHistory,
        browserLocation,
        settingsSection,
      })
    case SETTINGS_MODAL_PANE_KIND.COLUMN:
      return createElement(descriptor.component, {
        ...descriptor.componentProps,
        ...componentProps,
      })
    default:
      return null
  }
}
